import React, { useEffect, useState } from "react";
import { Avatar, CloseButton, Icon, useColorModeValue } from "@chakra-ui/react";
import { HStack, Box, VStack, Center, Wrap, WrapItem, Text, Heading, Link, Flex } from "@chakra-ui/layout";
import { Modal, ModalOverlay, ModalBody, ModalContent } from "@chakra-ui/modal";
import { Menu, MenuButton, MenuItem, MenuList, MenuDivider } from "@chakra-ui/menu";
import { useDisclosure } from "@chakra-ui/hooks";
import { IconButton, Button } from "@chakra-ui/button";
import { FiUser, FiTrendingUp, FiChevronUp, FiMenu, FiChevronDown, FiRepeat } from "react-icons/fi";
import { ImStack } from "react-icons/im";
import { BiWallet } from "react-icons/bi";
import { useMoralis } from "react-moralis";
import DeefyLogo from "./../assets/brand_logo/new_logo.jpg";
import metaMaskIcon from "./../assets/metamask.png";
import walletConnectIcon from "./../assets/walletconnect.png";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaOpencart, FaListAlt, FaWallet } from "react-icons/fa";

const LinkItems = [
  // { name: "Rental MarketPlace", icon: FaOpencart, identifier: "/rentalmarketplace"},
  { name: "PayLater MarketPlace", icon: FaOpencart, identifier: "/marketplace" },
  { name: "P2P Loan Requests", icon: ImStack, identifier: "/lend" },
  // { name: "Deefy Wallet", icon: FaWallet, identifier: "/wallet" },
  { name: "My Profile and NFTs", icon: FiRepeat, identifier: "/profile" },
  // { name: "My Loan Book", icon: FaListAlt, identifier: "/myloans" },
  // { name: "Trending", icon: FiTrendingUp, identifier: "/trending" },
];

const signinMessage = `Hi there from Deefy! Sign this message to prove that you have access to this wallet and we'll log you in. This won't cost you any Ether.`;

const MotionHStack = motion(HStack);
const MotionVStack = motion(VStack);

const SidebarNavContent = ({ onClose, ...rest }) => {
  const [currentActiveNav, setCurrentActiveNav] = useState("");

  return (
    <Box
      transition="3s ease"
      bg={useColorModeValue("white", "gray.900")}
      borderRight="1px"
      borderRightColor={useColorModeValue("gray.200", "gray.700")}
      w={{ base: "full", md: 60 }}
      pos="fixed"
      h="full"
      {...rest}
    >
      <Flex alignItems="center" mx="8" my="4" justifyContent="space-between">
        <Flex w="100%" align={"center"} direction={"column"}>
          <Avatar size={"lg"} src={DeefyLogo} />
        </Flex>
        <CloseButton display={{ base: "flex", md: "none" }} onClick={onClose} />
      </Flex>
      {LinkItems.map((link) => (
        <NavItem
          key={link.name}
          icon={link.icon}
          identifier={link.identifier}
          currentActiveNav={currentActiveNav}
          setCurrentActiveNav={setCurrentActiveNav}
          bg={currentActiveNav === link.identifier ? "brand.900" : ""}
          color={currentActiveNav === link.identifier ? "white" : ""}
          border={"1px solid white"}
          _hover={
            currentActiveNav === link.identifier
              ? {}
              : {
                  borderColor: "brand.900",
                }
          }
        >
          {link.name}
        </NavItem>
      ))}
    </Box>
  );
};

const NavItem = ({ icon, identifier, currentActiveNav, setCurrentActiveNav, children, ...rest }) => {
  return (
    <>
      <NavLink
        to={identifier}
        className={({ isActive }) => {
          if (isActive) setCurrentActiveNav(identifier);
          return "";
        }}
      >
        <Flex
          align="center"
          p="3"
          mx="4"
          borderRadius="lg"
          role="group"
          cursor="pointer"
          mb={2}
          fontWeight={600}
          {...rest}
        >
          {icon && <Icon mr="4" fontSize="20" as={icon} />}
          {children}
        </Flex>
      </NavLink>
    </>
  );
};

const ButtonWithSubmenu = ({ currentActiveNav, setCurrentActiveNav, icon }) => {
  const navigate = useNavigate();
  const { isOpen, onToggle } = useDisclosure();

  const boxVariant = {
    open: { opacity: 1, height: "auto" },
    collapsed: { opacity: 0, height: 0 },
  };

  const stackVariant = {
    open: { marginBottom: 8 },
    collapsed: { marginBottom: 0 },
  };

  return (
    <VStack mx="4" mb={2}>
      <MotionHStack
        p="4"
        w={"100%"}
        align="center"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        justify={"space-between"}
        fontWeight={600}
        variants={stackVariant}
        initial={false}
        animate={isOpen ? "open" : "collapsed"}
        transition={{ duration: 0.2, delay: 0.2, ease: "easeInOut" }}
        userSelect={"none"}
        onClick={() => {
          onToggle();
          setCurrentActiveNav("/profile");
          navigate("/profile");
        }}
        bg={currentActiveNav === "/profile" || currentActiveNav === "/myloans" ? "brand.900" : ""}
        color={currentActiveNav === "/profile" || currentActiveNav === "/myloans" ? "white" : ""}
        border={"1px solid white"}
        _hover={
          currentActiveNav === "/profile" || currentActiveNav === "/myloans"
            ? {}
            : {
                borderColor: "brand.900",
              }
        }
      >
        <Text> {icon && <Icon mr="4" fontSize="16" as={icon} />}Profile</Text>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </MotionHStack>
      <AnimatePresence>
        {isOpen && (
          <MotionVStack
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={boxVariant}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            w={"100%"}
            pl={4}
          >
            <Flex
              align="center"
              p="3"
              borderRadius="lg"
              role="group"
              cursor="pointer"
              fontWeight={600}
              w="100%"
              bg={currentActiveNav === "/profile" ? "brand.700" : ""}
              color={currentActiveNav === "/profile" ? "white" : ""}
              border={"1px solid white"}
              _hover={
                currentActiveNav === "/profile"
                  ? {}
                  : {
                      borderColor: "brand.700",
                    }
              }
              onClick={() => {
                setCurrentActiveNav("/profile");
                navigate("/profile");
              }}
            >
              My Nfts
            </Flex>
            <Flex
              align="center"
              p="3"
              borderRadius="lg"
              role="group"
              cursor="pointer"
              fontWeight={600}
              w="100%"
              bg={currentActiveNav === "/myloans" ? "brand.700" : ""}
              color={currentActiveNav === "/myloans" ? "white" : ""}
              border={"1px solid white"}
              _hover={
                currentActiveNav === "/myloans"
                  ? {}
                  : {
                      borderColor: "brand.700",
                    }
              }
              onClick={() => {
                setCurrentActiveNav("/myloans");
                navigate("/myloans");
              }}
            >
              My loans
            </Flex>
          </MotionVStack>
        )}
      </AnimatePresence>
    </VStack>
  );
};

const MobileNav = ({ onOpen, ...rest }) => {
  const { authenticate, isAuthenticated, isWeb3EnableLoading, isWeb3Enabled, enableWeb3 } = useMoralis();
  const { isOpen: isOpenModal, onOpen: onOpenModal, onClose: onCloseModal } = useDisclosure();

  // const injectWeb3 = async () => {
  //   await Moralis.enableWeb3();
  //   const web3Js = new Web3(Moralis.provider);
  //   window.web3 = web3Js;
  // };

  useEffect(() => {
    const connectorId = window.localStorage.getItem("connectorId");
    if (isAuthenticated && !isWeb3Enabled && !isWeb3EnableLoading) {
      enableWeb3({ provider: connectorId });
    }
    // if (isAuthenticated) injectWeb3();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isWeb3Enabled]);

  return (
    <>
      <Flex
        ml={{ base: 0, md: 60 }}
        px={{ base: 4, md: 4 }}
        height="20"
        alignItems="center"
        bg={useColorModeValue("white", "gray.900")}
        borderBottomWidth="1px"
        borderBottomColor={useColorModeValue("gray.200", "gray.700")}
        justifyContent={{ base: "space-between", md: "flex-end" }}
        {...rest}
      >
        <IconButton
          display={{ base: "flex", md: "none" }}
          onClick={onOpen}
          variant="outline"
          aria-label="open menu"
          icon={<FiMenu />}
        />

        <Box>
          {isAuthenticated ? (
            <LoggedInComponent />
          ) : (
            <Button
              leftIcon={<BiWallet size={"1.5em"} />}
              borderColor={"brand.900"}
              borderWidth={"2px"}
              color={"brand.900"}
              variant={"outline"}
              _hover={{ bg: "brand.900", color: "white" }}
              _focus={{}}
              boxShadow={"0 5px 20px 0px rgb(107 70 193 / 43%)"}
              size="md"
              onClick={onOpenModal}
            >
              Connect wallet
            </Button>
          )}
        </Box>
      </Flex>
      <Modal isOpen={isOpenModal} onClose={onCloseModal} isCentered size={"2xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Wrap spacing={0}>
              <WrapItem w={"50%"}>
                <Center
                  w="100%"
                  minH="200px"
                  borderRight="1px"
                  borderColor="gray.300"
                  borderLeftRadius="xl"
                  cursor={"pointer"}
                  _hover={{ bg: "gray.200" }}
                  onClick={() =>
                    authenticate({
                      onComplete: onCloseModal,
                      signingMessage: signinMessage,
                    })
                  }
                >
                  <VStack my={8} w="100%">
                    <Avatar size="2xl" bg="yellow.100" name="Meta Mask" src={metaMaskIcon} />
                    <Heading size="lg">MetaMask</Heading>
                    <Text fontWeight={600} color={"gray.500"} textAlign={"center"}>
                      Connect to your MetaMask Wallet
                    </Text>
                  </VStack>
                </Center>
              </WrapItem>
              <WrapItem w={"50%"}>
                <Center
                  w="100%"
                  minH="200px"
                  borderLeft="1px"
                  borderColor="gray.300"
                  borderRightRadius="xl"
                  cursor={"pointer"}
                  _hover={{ bg: "gray.200" }}
                  onClick={() =>
                    authenticate({ provider: "walletconnect", onComplete: onCloseModal, signingMessage: signinMessage })
                  }
                >
                  <VStack my={8} w="100%">
                    <Avatar size="2xl" name="Wallet Connect" src={walletConnectIcon} />
                    <Heading size="lg">WalletConnect</Heading>
                    <Text fontWeight={600} color={"gray.500"} textAlign={"center"}>
                      Scan with WalletConnect to connect
                    </Text>
                  </VStack>
                </Center>
              </WrapItem>
            </Wrap>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const LoggedInComponent = () => {
  const { user, logout } = useMoralis();
  const userWalletAddress = user.attributes.ethAddress;

  return (
    <HStack spacing={{ base: "0", md: "6" }}>
      <Flex alignItems={"center"}>
        <Menu autoSelect={false}>
          <MenuButton py={2} transition="all 0.3s" _focus={{ boxShadow: "none" }}>
            <HStack>
              <Avatar
                size={"md"}
                showBorder={true}
                borderColor={"gray.300"}
                bg={"white"}
                src={`https://avatars.dicebear.com/api/identicon/${user.get("username")}.svg`}
              />
              <Box display={{ base: "none", md: "flex" }}>
                <FiChevronDown />
              </Box>
            </HStack>
          </MenuButton>
          <MenuList
            zIndex={999}
            bg={useColorModeValue("white", "gray.900")}
            borderColor={useColorModeValue("gray.200", "gray.700")}
          >
            <Heading px={4} size={"sm"}>{`${userWalletAddress.slice(0, 6)}....${userWalletAddress.slice(-6)}`}</Heading>
            <MenuDivider />
            <MenuItem onClick={logout}>Sign out</MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </HStack>
  );
};

export { MobileNav, SidebarNavContent };
